import { createApp } from 'vue';
import VueApp from './App.vue';
import router from './router';
import store from './store';
import { i18n } from './i18n';
import { useI18n } from "vue-i18n";
import axios from 'axios';
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";

// Our default locale
import NL from './locales/nl.json'
import EN from './locales/en.json'
import DE from './locales/de.json'

// Bootstrap 5
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Image Compare Viewer
import 'image-compare-viewer/dist/image-compare-viewer.min.css';

// Overlay Scrollbars
import 'overlayscrollbars/css/OverlayScrollbars.min.css';

// Animate CSS
import 'animate.css/animate.min.css';

// Custom Fonts
import '@fontsource/montserrat/variable.css';

// Custom Stylesheet
import '@/assets/css/variables.css';
import '@/assets/css/style.css';

(async() => {
    try {
        let baseUrl = process.env.NODE_ENV === 'production' ? process.env.VUE_APP_BACKEND_API_PROD : process.env.VUE_APP_BACKEND_API_DEV;

        if (typeof websiteUrl !== 'undefined') {
            baseUrl = websiteUrl + '/wp-json/web-musea/v1';
        }

        // We use this to override localhost so we can access the proper urls from LAN
        let customIp = '';

        if (process.env.VUE_APP_CUSTOM_IP) {
            customIp = `?custom_ip=${process.env.VUE_APP_CUSTOM_IP}`;
        }

        // Get the settings from our backend
        const settings = await axios.get(`${baseUrl}/settings${customIp}`);

        // Store the settings, so we can access them later when needed
        store.commit('setSettings', settings.data);

        // Get the localizations from the backend
        const locale = await axios.get(`${baseUrl}/languages${customIp}`);

        // Merge all the locale with our base
        if (settings.data.languages.includes('en')) {
            i18n.global.mergeLocaleMessage('en', locale.data.en);
        }

        if (settings.data.languages.includes('de')) {
            i18n.global.mergeLocaleMessage('de', locale.data.de);
        }

        if (settings.data.languages.includes('nl')) {
            i18n.global.mergeLocaleMessage('nl', locale.data.nl);
        }

        document.documentElement.style.setProperty('--global-background', store.state.settings.colors.other.background_color);

        // Create our vue app
        const app = createApp(VueApp);
        app.use(i18n);
        app.use(store);
        app.use(router);

        // Global variables and functions
        app.mixin({
            data() {
                return {
                    overlayVisible: false,
                    containerOverflowing: false,
                    localeChangeEvent: new Event('locale_changed'),
                };
            },
            setup() {
                const { t, te, tm } = useI18n();
                return { t, te, tm };
            },
            methods: {
                localeT(locale) {
                    const { t, te, tm } = useI18n();
                    return t(locale);
                },
                // Not sure if we still need this functions
                showLogo(event) {
                    document.documentElement.style.setProperty('--global-logo-visibilty', 'visible');
                },
                hideLogo(event) {
                    document.documentElement.style.setProperty('--global-logo-visibilty', 'hidden');
                },
                // If we should show a scroll hint on the overlay
                showScrollHint() {
                    let elem = document.getElementById("museum-container");

                    return elem.scrollHeight > elem.clientHeight
                },
                hideOverlay(event) {
                    this.overlayVisible = false;
                    document.documentElement.style.setProperty('--global-overlay-status', 'hidden');
                    enableBodyScroll(document.body);
                },
                showOverlay(event) {
                    this.overlayVisible = true;
                    document.documentElement.style.setProperty('--global-overlay-status', 'visible');
                    disableBodyScroll(document.body);
                },
                changeLocale(locale) {
                    this.$i18n.locale = locale;
                    window.dispatchEvent(this.localeChangeEvent);
                },
                getMonthName(month) {
                    const { t, te, tm } = useI18n();
                    switch (month) {
                        case 0:
                            return t('date_info.january');
                        case 1:
                            return t('date_info.february');
                        case 2:
                            return t('date_info.march');
                        case 3:
                            return t('date_info.april');
                        case 4:
                            return t('date_info.may');
                        case 5:
                            return t('date_info.june');
                        case 6:
                            return t('date_info.july');
                        case 7:
                            return t('date_info.august');
                        case 8:
                            return t('date_info.september');
                        case 9:
                            return t('date_info.october');
                        case 10:
                            return t('date_info.november');
                        case 11:
                            return t('date_info.december');
                        default:
                            return "Onbekende maand";
                    }
                },
                getDayName(day) {
                    switch (day) {
                        case 0:
                            return t('date_info.monday');
                        case 1:
                            return t('date_info.tuesday');
                        case 2:
                            return t('date_info.wednesday');
                        case 3:
                            return t('date_info.thursday');
                        case 4:
                            return t('date_info.friday');
                        case 5:
                            return t('date_info.saturday');
                        case 6:
                            return t('date_info.sunday');
                    }
                }
            }
        });

        app.mount('#app');
    } catch (error) {
        // We could not connect properly to our backend, let the user know about this
        var message = document.getElementById('start-message');
        message.innerHTML = `Could not connect to ${process.env.VUE_APP_TITLE} backend`;
        // Useful if we wanna debug
        console.log(error);
    }
})()